import { STRIPE_RETURN_PARAMS } from "@ergon-ai/utils";

/**
 * Query parameters that must never reach the observability store.
 *
 * Stripe appends the intent id and its client secret to the checkout's
 * `return_url` after a bank redirect. The page removes them from the address
 * bar in its first layout effect, but that removal is itself a
 * `history.replaceState`, and Sentry's history instrumentation runs its
 * handlers BEFORE the replacement applies
 * (`@sentry-internal/browser-utils/.../instrument/history.js`, `triggerHandlers`
 * ahead of `originalHistoryFunction.apply`). On a fresh landing the previous
 * href is unset, so the breadcrumb falls back to `window.location.href`, which
 * is still the dirty one (`@sentry/browser/.../integrations/breadcrumbs.js`,
 * `parsedFrom = parsedLoc`). The redaction stays here; the NAMES are
 * `@ergon-ai/utils`'s `STRIPE_RETURN_PARAMS` (EAH-2525), re-exported.
 */
export { STRIPE_RETURN_PARAMS };

/** The older list: credentials that used to be the only thing scrubbed. */
export const CREDENTIAL_PARAMS = [
  "token",
  "password",
  "apikey",
  "token_hash",
] as const;

const REDACTED = "[REDACTED]";

// No `\b` before the names: `_` is a word character, so an anchor there would
// stop matching `access_token=` and `refresh_token=`, which the rule this
// replaced did redact. The `=` is the delimiter that matters.
const PATTERN = new RegExp(
  `(${[...CREDENTIAL_PARAMS, ...STRIPE_RETURN_PARAMS].join("|")})=[^&#\\s]*`,
  "gi",
);

/**
 * Replaces every sensitive query value with `[REDACTED]`, leaving the rest of
 * the URL readable. Callers pass a string: a breadcrumb field is typed loose,
 * so they narrow it first rather than this widening its own contract.
 */
export function redactSensitiveUrl(value: string): string {
  return value.replace(PATTERN, `$1=${REDACTED}`);
}

/**
 * Headers that never leave the machine, on any event kind. Sentry attaches
 * `request.headers` to a transaction the same way it does to an error
 * (`@sentry/core` `integrations/requestdata.js`, `addNormalizedRequestDataToEvent`
 * spreads the normalised request onto `event.request` with no type filter), so
 * the two error hooks and the two transaction hooks all have to strip them.
 */
export const SENSITIVE_HEADERS = [
  "authorization",
  "cookie",
  "x-api-key",
  "x-forwarded-for",
] as const;

/** Removes those headers in place. Safe on an event that carries none. */
export function deleteSensitiveHeaders(request: {
  headers?: Record<string, unknown>;
}): void {
  if (!request.headers) return;
  for (const name of SENSITIVE_HEADERS) delete request.headers[name];
}

/**
 * The request line rides on a transaction too, and `beforeSend` never sees
 * one: `@sentry/core` `client.js` routes error events to `beforeSend` and
 * transactions to `beforeSendTransaction`. The same three carriers plus the
 * span data the browser and server integrations write (`url.full`,
 * `url.query`, `http.target`).
 */
export function redactTransactionEvent<
  T extends {
    request?: {
      url?: string;
      query_string?: unknown;
      headers?: Record<string, unknown>;
    };
    contexts?: { trace?: { data?: Record<string, unknown> } };
  },
>(event: T): T {
  if (event.request?.url) {
    event.request.url = redactSensitiveUrl(event.request.url);
  }
  if (event.request && typeof event.request.query_string === "string") {
    event.request.query_string = redactSensitiveUrl(event.request.query_string);
  }
  if (event.request) deleteSensitiveHeaders(event.request);
  const data = event.contexts?.trace?.data;
  if (data) {
    for (const [key, value] of Object.entries(data)) {
      if (typeof value === "string") data[key] = redactSensitiveUrl(value);
    }
  }
  return event;
}
